import { useLocalizationContext } from '@im/react-localization';
import { useIsAuthenticated, useOktaContext } from 'mf_core/auth';
import { useLogoutContext } from 'mf_core/logout';
import { ExtraHeaders, ObjectMap, useApiQuery } from 'mf_core/query';
import { UseQueryOptions } from '@tanstack/react-query';

/** Wrapper for CMS API queries to handle logout mode operations */
export function useCMSApiQuery<TO = ObjectMap>(
  route: string,
  params?: ObjectMap,
  useQueryOptions?: Partial<UseQueryOptions<TO>>,
  extraHeaders?: ExtraHeaders
) {
  const isAuthenticated = useIsAuthenticated();
  const cmsData = useLogoutContext();
  route = isAuthenticated ? route : route.replace('/cms/v1', '/cms-noauth/v1');

  return useApiQuery<TO>(route, params, useQueryOptions, extraHeaders, {
    isLogoutApi: !!cmsData?.data?.isLogoutEnabled,
  });
}

/** Wrapper for CMS API queries to handle logout mode operations using OKTA Context */
export function useCMSApiOktaContextQuery<TO = ObjectMap>(
  route: string,
  params?: ObjectMap,
  useQueryOptions?: UseQueryOptions<TO>,
  extraHeaders?: ExtraHeaders
) {
  const oktaContext = useOktaContext();
  const isAuthenticated = oktaContext?.authState?.isAuthenticated;
  const cmsData = useLogoutContext();
  route = isAuthenticated ? route : route.replace('/cms/v1', '/cms-noauth/v1');

  return useApiQuery<TO>(route, params, useQueryOptions, extraHeaders, {
    isLogoutApi: !!cmsData?.data?.isLogoutEnabled,
  });
}

/** Wrapper for Dashboard widget API queries to handle refetch operations */
export function useCMSWidgetApiQuery<TO = ObjectMap>(
  route: string,
  params?: ObjectMap,
  useQueryOptions?: UseQueryOptions<TO>,
  extraHeaders?: ExtraHeaders
) {
  const { locale } = useLocalizationContext();
  const apiParams: ObjectMap = { ...params, lang: locale };
  const apiQueryOptions: Partial<UseQueryOptions<TO>> = {
    ...useQueryOptions,
    gcTime: Infinity,
    staleTime: Infinity,
  };

  return useApiQuery<TO>(route, apiParams, apiQueryOptions, extraHeaders);
}
